import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Clock, Camera, Coffee, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MethodIcon } from './MethodIcon';

function getInitials(entry) {
  const first = entry.first_name || entry.display_name || '';
  const last = entry.last_name || '';
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return '??';
}

function getDisplayName(entry) {
  if (entry.display_name) return entry.display_name;
  const parts = [entry.first_name, entry.last_name].filter(Boolean);
  return parts.join(' ') || 'Unknown';
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(checkInTime, checkOutTime) {
  if (!checkInTime || !checkOutTime) return '';
  const diffMs = new Date(checkOutTime) - new Date(checkInTime);
  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
}

const EVENT_CONFIG = {
  clock_in: {
    dotColor: 'bg-emerald-500',
    label: (entry) => <>Clocked in at {formatTime(entry.time)}</>,
    icon: null, // uses MethodIcon
  },
  clock_out: {
    dotColor: 'bg-gray-300',
    label: (entry) => (
      <>
        Clocked out at {formatTime(entry.time)}
        {entry.check_in_time && entry.check_out_time && (
          <span className="ml-1">
            ({formatDuration(entry.check_in_time, entry.check_out_time)})
          </span>
        )}
      </>
    ),
    icon: null,
  },
  break_start: {
    dotColor: 'bg-amber-400',
    label: (entry) => <>Started break at {formatTime(entry.time)}</>,
    icon: Coffee,
  },
  break_end: {
    dotColor: 'bg-blue-400',
    label: (entry) => (
      <>
        Ended break at {formatTime(entry.time)}
        {entry.duration_minutes != null && (
          <span className="ml-1">({entry.duration_minutes}m)</span>
        )}
      </>
    ),
    icon: Play,
  },
};

export function RecentActivityFeed({ activityFeed = [], onViewSelfie }) {
  return (
    <Card className="flex-1 flex flex-col overflow-hidden py-0">
      <CardHeader className="px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm">Recent Activity</CardTitle>
          <Badge variant="secondary" className="text-xs h-5 px-1.5">
            {activityFeed.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-full">
          {activityFeed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-1">
              <Clock className="h-8 w-8 text-muted-foreground opacity-30" />
              <p className="text-xs text-muted-foreground">No recent activity</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {activityFeed.map((entry) => {
                const config = EVENT_CONFIG[entry.type] || EVENT_CONFIG.clock_in;
                const name = getDisplayName(entry);
                const initials = getInitials(entry);
                const EventIcon = config.icon;

                return (
                  <div
                    key={entry.id}
                    className="flex items-center gap-2.5 px-4 py-2 hover:bg-muted/30 transition-colors"
                  >
                    {/* Timeline dot */}
                    <span
                      className={cn(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        config.dotColor
                      )}
                    />

                    {/* Mini avatar */}
                    <Avatar className="h-7 w-7 shrink-0">
                      {entry.avatar_url && (
                        <AvatarImage src={entry.avatar_url} alt={name} />
                      )}
                      <AvatarFallback className="text-[10px] font-medium">
                        {initials}
                      </AvatarFallback>
                    </Avatar>

                    {/* Name + action */}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground truncate">
                        {name}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {config.label(entry)}
                      </p>
                    </div>

                    {/* Event-specific icon */}
                    {EventIcon ? (
                      <EventIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
                    ) : entry.check_in_method ? (
                      <MethodIcon
                        method={entry.check_in_method}
                        size="h-3 w-3"
                        className="shrink-0"
                      />
                    ) : null}

                    {/* Selfie camera icon */}
                    {entry.selfie_url && (
                      <button
                        onClick={() => onViewSelfie?.(entry.selfie_url)}
                        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
                      >
                        <Camera className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
